/**
 * OpenClaw gateway WebSocket client — the protocol/transport layer.
 *
 * Hosts call `createOpenClawClient(url, config)` with their own
 * clientIdPrefix, token storage, and health-monitoring strategy.
 *
 * Reconciliation of the two host forks:
 *   - Paradex (heartbeat watchdog, sendUiEvent + ui.catalog.request,
 *     pairing-role + pairing-token retry, hex-slice clientId)
 *   - Desktop (circuit breaker, fallback pairing token + localStorage
 *     seeding, always 'operator' role, defensive clientId)
 *
 * Differences captured as `OpenClawClientConfig` flags so each host
 * keeps its existing behavior bit-for-bit while sharing this body.
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  AgentConnectionStatus,
  GatewayFrame,
  GatewayRequest,
} from './types.js';

// --- Re-exported constants ---

export const DEFAULT_OPERATOR_SCOPES = [
  'operator.admin',
  'operator.read',
  'operator.write',
  'operator.approvals',
] as const;

// --- Public client interface ---

export interface AgentWebSocket {
  /**
   * Send a chat message. When `opts.sessionKey` is provided it overrides
   * the gateway's snapshot `mainSessionKey` so each UI-side chat session
   * gets its own isolated agent context.
   */
  readonly send: (
    content: string,
    opts?: { readonly sessionKey?: string },
  ) => void;
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
  /**
   * Request a self-service pairing with the gateway. The user approves
   * out-of-band (e.g. `openclaw devices approve --latest`); the gateway
   * emits `node.pair.resolved` with a freshly minted token. The returned
   * promise resolves with that token (also persisted via tokenProvider).
   */
  readonly requestPairing: (params?: {
    readonly scopes?: readonly string[];
  }) => Promise<string>;
  /**
   * Forward a UI interaction (button click, form submit) from a rendered
   * catalog component or MCP iframe back to the agent. No-op when the
   * client wasn't configured with a `catalogManifest`.
   */
  readonly sendUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
  readonly onMessage: (handler: (frame: GatewayFrame) => void) => () => void;
  readonly onStatusChange: (
    handler: (status: AgentConnectionStatus, errorMessage?: string) => void,
  ) => () => void;
  readonly getStatus: () => AgentConnectionStatus;
  readonly close: () => void;
  readonly reconnect: () => void;
}

// --- Token storage contract (host-supplied) ---

export interface AgentTokenProvider {
  getAgentToken(accountId: string): string | null;
  setAgentToken(accountId: string, token: string): void;
  /**
   * Optional — used only when `supportsPairingRole` is true. Paradex
   * exposes this for its pairing-token retry path; Desktop omits it.
   */
  clearAgentToken?(accountId: string): void;

  getDeviceToken(accountId: string): string | null;
  setDeviceToken(accountId: string, token: string): void;
  clearDeviceToken(accountId: string): void;
}

// --- Device handshake contract (host-supplied) ---

export interface AgentDeviceSigner {
  buildDeviceHandshake(input: {
    clientId: string;
    clientMode: string;
    role: string;
    scopes: readonly string[];
    token: string;
    nonce: string;
  }): Promise<{
    readonly id: string;
    readonly publicKey: string;
    readonly signature: string;
    readonly signedAt: number;
    readonly nonce: string;
  }>;
}

// --- Client configuration ---

export interface OpenClawClientConfig {
  /** e.g. `'paradex-dime-terminal'` or `'paradigm-block-terminal'`. */
  clientIdPrefix: string;
  /** Account-scoped identifier — Paradex address or Paradigm userId. */
  accountId: string;
  /** Client mode — defaults to `'webchat'`. */
  clientMode?: string;
  /** Operator scopes — defaults to `DEFAULT_OPERATOR_SCOPES`. */
  scopes?: readonly string[];

  tokenProvider: AgentTokenProvider;
  deviceSigner: AgentDeviceSigner;

  /**
   * When true, the client retries with the pairing token after a device-
   * token rejection, and falls back to no-token pairing-role mode after a
   * pairing-token rejection. Paradex: true. Desktop: false.
   *
   * Requires `tokenProvider.clearAgentToken` to also be set.
   */
  supportsPairingRole?: boolean;

  /**
   * One-time fallback pairing token. When the local store has no
   * agent-token but the caller knows one (e.g. synced from server-side
   * preferences), this is used for the handshake. On first successful
   * auth we seed it into `tokenProvider.setAgentToken` so reconnects
   * don't need it again. Desktop-only; Paradex passes undefined.
   */
  fallbackPairingToken?: string;

  /**
   * Optional diagnostics hook. Called for connection lifecycle events — the
   * connect target, socket open/close (with close `code`/`reason`), socket
   * errors, retry exhaustion, and every status transition — so a host can log
   * why a connection stalls. Omit ⇒ no logging.
   */
  onDebug?: (event: string, detail?: Record<string, unknown>) => void;

  /** Health monitoring strategy. Paradex: 'heartbeat'. Desktop: 'circuit-breaker'. */
  healthStrategy: 'heartbeat' | 'circuit-breaker';
  heartbeatIntervalMs?: number;
  heartbeatDeadAfterMs?: number;
  circuitFailureThreshold?: number;
  circuitFailureWindowMs?: number;

  /**
   * Opaque catalog manifest the client returns on `ui.catalog.request`.
   * When undefined the catalog handler is a no-op and `sendUiEvent`
   * still works as a thin pass-through (the agent decides whether to
   * accept). Paradex passes `AGENT_CATALOG_MANIFEST`; Desktop passes
   * undefined.
   */
  catalogManifest?: unknown;

  /** Custom clientId-suffix policy. Defaults to defensive 6+4 slice. */
  clientIdSuffix?(accountId: string): string;
}

// --- Helpers ---

let nextReqId = 1;

function makeReqId(): string {
  return String(nextReqId++);
}

// Upper bound on how long a `requestPairing` promise stays pending. The token
// arrives via a later `node.pair.resolved`, so a socket that stays OPEN but
// never delivers it would otherwise leak the promise (and its `pendingPairings`
// entry) forever. Generous because it gates on a human approving the device.
const PAIRING_TIMEOUT_MS = 120_000;

/**
 * Default suffix policy — defensive length check, then 6+4 slice. Covers
 * both Paradex hex addresses (always >= 10 chars → 6+4 hex slice) and
 * Paradigm userIds (may be shorter → full string fallback).
 */
export function defaultClientIdSuffix(accountId: string): string {
  const lower = accountId.toLowerCase();
  return lower.length >= 10 ? `${lower.slice(0, 6)}${lower.slice(-4)}` : lower;
}

/**
 * Convenience clientId builder used by the standalone pairing screen
 * and the AgentWebSocket factory.
 */
export function buildClientId(
  clientIdPrefix: string,
  accountId: string,
  suffix: (accountId: string) => string = defaultClientIdSuffix,
): string {
  return `${clientIdPrefix}:${suffix(accountId)}`;
}

/**
 * True if the gateway error looks like a stale/rotated token rather than
 * a permanent auth failure (device revoked, signature invalid). We only
 * auto-retry on token errors so we don't thrash on real failures.
 */
function isTokenAuthError(message: string | null | undefined): boolean {
  if (message == null) return false;
  return /token.mismatch|token.expired|expired.token|invalid.token/iu.test(
    message,
  );
}

// --- Factory ---

export function createOpenClawClient(
  url: string,
  config: OpenClawClientConfig,
): AgentWebSocket {
  const {
    clientIdPrefix,
    accountId,
    clientMode = 'webchat',
    scopes: configuredScopes,
    tokenProvider,
    deviceSigner,
    supportsPairingRole = false,
    fallbackPairingToken,
    healthStrategy,
    heartbeatIntervalMs = 15_000,
    heartbeatDeadAfterMs = 60_000,
    circuitFailureThreshold = 5,
    circuitFailureWindowMs = 60_000,
    catalogManifest,
    clientIdSuffix = defaultClientIdSuffix,
    onDebug,
  } = config;

  const defaultScopes = configuredScopes ?? DEFAULT_OPERATOR_SCOPES;
  const clientId = buildClientId(clientIdPrefix, accountId, clientIdSuffix);
  const debug = (event: string, detail?: Record<string, unknown>): void => {
    onDebug?.(event, detail);
  };

  let status: AgentConnectionStatus = 'connecting';
  let connectReqId: string | null = null;
  let sessionKey: string | null = null;
  let lastHandshakeUsedDeviceToken = false;
  let lastHandshakeUsedPairingToken = false;
  let deviceTokenRetryAttempted = false;
  let pairingTokenRetryAttempted = false;

  // Circuit-breaker state (only used when healthStrategy === 'circuit-breaker').
  let consecutiveFailures = 0;
  let firstFailureAt = 0;
  let circuitBroken = false;

  // Heartbeat state (only used when healthStrategy === 'heartbeat').
  let lastFrameAt = Date.now();
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  const messageHandlers = new Set<(frame: GatewayFrame) => void>();
  const statusHandlers = new Set<
    (status: AgentConnectionStatus, errorMessage?: string) => void
  >();
  const pendingRequests = new Map<
    string,
    { resolve: (v: unknown) => void; reject: (e: Error) => void }
  >();
  const pendingPairings = new Set<{
    resolve: (token: string) => void;
    reject: (e: Error) => void;
  }>();

  function setStatus(newStatus: AgentConnectionStatus, errorMsg?: string) {
    if (newStatus !== status || errorMsg != null) {
      debug('status', { from: status, to: newStatus, errorMsg });
    }
    status = newStatus;
    for (const handler of statusHandlers) {
      handler(newStatus, errorMsg);
    }
  }

  function sendReq(method: string, params?: Record<string, unknown>): string {
    const id = makeReqId();
    const req: GatewayRequest = { type: 'req', id, method, params };
    ws.send(JSON.stringify(req));
    return id;
  }

  const maxRetries = 3;
  debug('connect', {
    url,
    clientId,
    healthStrategy,
    connectionTimeoutMs: 10_000,
  });
  const ws = new ReconnectingWebSocket(url, [], {
    maxRetries,
    connectionTimeout: 10_000,
    maxReconnectionDelay: 30_000,
  });

  // ---- Heartbeat watchdog (Paradex's strategy) ----

  function stopHeartbeat() {
    if (heartbeatTimer != null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function startHeartbeat() {
    if (healthStrategy !== 'heartbeat') return;
    stopHeartbeat();
    lastFrameAt = Date.now();
    heartbeatTimer = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      // Passive check — OpenClaw streams health events frequently, so any
      // working socket updates lastFrameAt without us probing. True silence
      // past the dead-after threshold means the connection is half-open
      // (NAT / corporate proxy dropped it silently); force a reconnect.
      if (Date.now() - lastFrameAt > heartbeatDeadAfterMs) {
        ws.reconnect();
      }
    }, heartbeatIntervalMs);
  }

  // ---- Lifecycle ----

  ws.addEventListener('open', () => {
    debug('open', { url, retryCount: ws.retryCount });
    setStatus('connected');
    lastFrameAt = Date.now();
  });

  ws.addEventListener('close', (event) => {
    // The close `code`/`reason` is the single most useful signal for a stalled
    // connection: 1006 = couldn't reach / abnormal closure (bad URL, gateway
    // down, TLS/proxy); 1008 / 4xxx = policy or auth rejection; 1000/1001 =
    // clean. `retryCount` vs maxRetries tells us if we've given up.
    debug('close', {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
      retryCount: ws.retryCount,
      maxRetries,
    });
    setStatus('disconnected');
    connectReqId = null;
    stopHeartbeat();

    // reconnecting-websocket stops after `maxRetries` and emits no terminal
    // event of its own. Surface a clear "give up" error so the UI doesn't sit
    // on a stale state forever (the circuit-breaker path below only runs for
    // Desktop; heartbeat hosts like the standalone need this).
    if (
      healthStrategy !== 'circuit-breaker' &&
      !event.wasClean &&
      ws.retryCount >= maxRetries
    ) {
      debug('retries-exhausted', { url, retryCount: ws.retryCount });
      setStatus(
        'error',
        `Could not reach the gateway after ${maxRetries + 1} attempts (last close code ${event.code}). Check the gateway URL, then reconnect to retry.`,
      );
      return;
    }

    if (healthStrategy === 'circuit-breaker') {
      if (circuitBroken) return;
      const now = Date.now();
      if (
        firstFailureAt === 0 ||
        now - firstFailureAt > circuitFailureWindowMs
      ) {
        firstFailureAt = now;
        consecutiveFailures = 1;
        return;
      }
      consecutiveFailures += 1;
      if (consecutiveFailures >= circuitFailureThreshold) {
        circuitBroken = true;
        setStatus(
          'error',
          'Terminal gateway unreachable after repeated attempts. Reconnect manually to retry.',
        );
        // Permanent close — `reconnecting-websocket` won't auto-retry after
        // `.close()`, so the loop stops until `reconnect()` resets it.
        ws.close();
      }
    }
  });

  ws.addEventListener('error', (event) => {
    // ReconnectingWebSocket's error event carries an optional `message`.
    const message = (event as { message?: string }).message;
    debug('error', { message, retryCount: ws.retryCount });
    setStatus('error', message);
  });

  // ---- Handshake ----

  async function handleChallenge(
    payload: { nonce?: string } | undefined,
  ): Promise<void> {
    setStatus('authenticating');
    const deviceToken = tokenProvider.getDeviceToken(accountId);
    const storedAgentToken = tokenProvider.getAgentToken(accountId);
    // Desktop uses an explicit fallback pairing token when localStorage
    // is empty — useful for new-device flows where the caller has the
    // token from server-synced preferences. Paradex passes undefined here.
    const pairingToken = storedAgentToken ?? fallbackPairingToken ?? null;
    const authToken = deviceToken ?? pairingToken ?? '';
    const isPairingMode = authToken === '';
    lastHandshakeUsedDeviceToken = deviceToken != null;
    lastHandshakeUsedPairingToken = deviceToken == null && pairingToken != null;

    const nonce =
      payload?.nonce ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

    const role = 'operator';
    // Paradex switches scopes to ['operator.pairing'] when the handshake
    // has no token; Desktop's gateway is configured for token-auth only
    // and rejects the 'pairing' scope, so it always uses defaultScopes.
    const scopes =
      supportsPairingRole && isPairingMode
        ? ['operator.pairing']
        : defaultScopes;

    let device;
    try {
      // Canonical v2 payload: the signed fields MUST match the connect
      // params verbatim — clientId / clientMode / role / scopes / token /
      // nonce — or the gateway rejects with "device identity mismatch".
      device = await deviceSigner.buildDeviceHandshake({
        clientId: 'webchat-ui',
        clientMode,
        role,
        scopes,
        token: authToken,
        nonce,
      });
    } catch {
      device = undefined; // Signing unavailable — server may accept anyway
    }

    connectReqId = sendReq('connect', {
      minProtocol: 3,
      maxProtocol: 3,
      role,
      scopes,
      client: {
        // The gateway schema accepts the fixed constant 'webchat-ui'.
        // Per-account namespacing is handled by the device.id / auth token
        // stored via the token provider, keyed by accountId.
        id: 'webchat-ui',
        version: '1.0.0',
        platform: 'browser',
        mode: clientMode,
      },
      auth: { token: authToken },
      device,
    });
  }

  // ---- Frame handling ----

  ws.addEventListener('message', (event: MessageEvent) => {
    // ANY frame from the server — including JSON we can't parse — is proof
    // the socket is alive, so refresh the heartbeat watchdog before parsing.
    lastFrameAt = Date.now();

    const rawData =
      typeof event.data === 'string' ? event.data : String(event.data);

    let frame: GatewayFrame;
    try {
      frame = JSON.parse(rawData) as GatewayFrame;
    } catch {
      return;
    }

    if (frame.type === 'event') {
      const evt = frame;

      if (evt.event === 'connect.challenge') {
        handleChallenge(evt.payload as { nonce?: string } | undefined).catch(
          () => {
            // swallow: errors surface via setStatus('error') inside handler
          },
        );
        return;
      }

      if (evt.event === 'ui.catalog.request' && catalogManifest != null) {
        sendReq('ui.catalog.response', {
          catalog: catalogManifest as Record<string, unknown>,
        });
        return;
      }

      if (evt.event === 'node.pair.resolved') {
        const newToken = (evt.payload as { token?: string } | undefined)?.token;
        if (newToken != null && newToken.length > 0) {
          tokenProvider.setAgentToken(accountId, newToken);
          for (const p of pendingPairings) p.resolve(newToken);
          pendingPairings.clear();
        }
        return;
      }
    }

    if (frame.type === 'res') {
      const res = frame;

      if (connectReqId != null && res.id === connectReqId) {
        connectReqId = null;
        if (res.ok) {
          const payload = res.payload as
            | {
                snapshot?: {
                  sessionDefaults?: { mainSessionKey?: string };
                };
                auth?: { deviceToken?: string };
              }
            | undefined;
          sessionKey =
            payload?.snapshot?.sessionDefaults?.mainSessionKey ?? null;
          const newDeviceToken = payload?.auth?.deviceToken;
          if (newDeviceToken != null && newDeviceToken.length > 0) {
            tokenProvider.setDeviceToken(accountId, newDeviceToken);
          }
          // Desktop-only: seed localStorage with the fallback pairing
          // token so future reconnects don't need it passed again.
          if (
            fallbackPairingToken != null &&
            fallbackPairingToken.length > 0 &&
            tokenProvider.getAgentToken(accountId) == null
          ) {
            tokenProvider.setAgentToken(accountId, fallbackPairingToken);
          }
          deviceTokenRetryAttempted = false;
          pairingTokenRetryAttempted = false;
          if (healthStrategy === 'circuit-breaker') {
            // Reaching `authenticated` means the URL + tokens are healthy;
            // any prior transient failures are stale and shouldn't count
            // toward the breaker on a future blip.
            consecutiveFailures = 0;
            firstFailureAt = 0;
          }
          setStatus('authenticated');
          startHeartbeat();
        } else if (
          lastHandshakeUsedDeviceToken &&
          !deviceTokenRetryAttempted &&
          isTokenAuthError(res.error?.message)
        ) {
          // Gateway rejected our stored device token (likely rotated
          // server-side). The Ed25519 device signature still proves the
          // same device, so clearing the stale token and reconnecting
          // falls back to the pairing token and the gateway re-issues a
          // fresh device token in `hello-ok`.
          //
          // Convergence note (PR 2 reconciliation): the Desktop original
          // retried on ANY auth failure here (no `isTokenAuthError`
          // check); Paradex restricted to token-error messages. The
          // package converges on Paradex's stricter check so a permanent
          // failure like "device revoked" doesn't reconnect-loop. This
          // is a deliberate, host-equal behavior change for Desktop —
          // safer, no new error path (the else branch still surfaces
          // `error` with the gateway message).
          deviceTokenRetryAttempted = true;
          tokenProvider.clearDeviceToken(accountId);
          setStatus('connecting');
          ws.reconnect();
        } else if (
          supportsPairingRole &&
          lastHandshakeUsedPairingToken &&
          !pairingTokenRetryAttempted &&
          isTokenAuthError(res.error?.message) &&
          tokenProvider.clearAgentToken != null
        ) {
          // Paradex-only path. The pairing token is single-use and was
          // consumed on first connect, so it can't be retried as-is.
          // Clear it and reconnect — the signed Ed25519 handshake is
          // still proof of identity for the device already in
          // `openclaw devices list`, so the gateway can re-issue both
          // tokens silently.
          pairingTokenRetryAttempted = true;
          tokenProvider.clearAgentToken(accountId);
          setStatus('connecting');
          ws.reconnect();
        } else {
          const errMsg = res.error?.message ?? 'Authentication failed';
          setStatus('error', errMsg);
          ws.close();
          for (const handler of messageHandlers) {
            handler(frame);
          }
        }
        return;
      }

      const pending = pendingRequests.get(res.id);
      if (pending != null) {
        pendingRequests.delete(res.id);
        if (res.ok) {
          pending.resolve(res.payload);
        } else {
          pending.reject(new Error(res.error?.message ?? 'Request failed'));
        }
        return;
      }
    }

    for (const handler of messageHandlers) {
      handler(frame);
    }
  });

  // ---- Public API ----

  return {
    send(content: string, opts?: { readonly sessionKey?: string }) {
      // Per-conversation sessionKey from the UI wins; otherwise fall back
      // to the gateway's snapshot `mainSessionKey`.
      const effectiveSessionKey = opts?.sessionKey ?? sessionKey ?? undefined;
      sendReq('chat.send', {
        sessionKey: effectiveSessionKey,
        idempotencyKey: `msg-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        message: content,
      });
    },

    async request<T = unknown>(
      method: string,
      params?: Record<string, unknown>,
    ): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        if (ws.readyState !== WebSocket.OPEN) {
          reject(new Error('Gateway not connected'));
          return;
        }
        const id = makeReqId();
        pendingRequests.set(id, {
          resolve: resolve as (v: unknown) => void,
          reject,
        });
        const req: GatewayRequest = { type: 'req', id, method, params };
        ws.send(JSON.stringify(req));
      });
    },

    sendUiEvent({
      surfaceId,
      componentId,
      event,
      data,
    }: {
      readonly surfaceId: string;
      readonly componentId?: string;
      readonly event: string;
      readonly data?: Record<string, unknown>;
    }) {
      if (ws.readyState !== WebSocket.OPEN) return;
      sendReq('ui.event', { surfaceId, componentId, event, data });
    },

    async requestPairing({
      scopes = defaultScopes,
    }: { readonly scopes?: readonly string[] } = {}) {
      if (ws.readyState !== WebSocket.OPEN) {
        throw new Error('Gateway not connected');
      }
      return new Promise<string>((resolve, reject) => {
        // Wrap settle so the timeout, node.pair.resolved, and close paths all
        // go through one guarded path that clears the timer exactly once.
        let settled = false;
        const entry = {
          resolve: (token: string) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(token);
          },
          reject: (e: Error) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            reject(e);
          },
        };
        pendingPairings.add(entry);
        const timer = setTimeout(() => {
          pendingPairings.delete(entry);
          entry.reject(new Error('Pairing request timed out'));
        }, PAIRING_TIMEOUT_MS);
        const id = makeReqId();
        pendingRequests.set(id, {
          resolve: () => {
            // res ok just means the request was accepted; token arrives
            // via node.pair.resolved which resolves pendingPairings.
          },
          reject: (e) => {
            for (const p of pendingPairings) p.reject(e);
            pendingPairings.clear();
          },
        });
        const req: GatewayRequest = {
          type: 'req',
          id,
          method: 'node.pair.request',
          params: {
            clientId,
            role: 'operator',
            scopes,
          },
        };
        ws.send(JSON.stringify(req));
      });
    },

    onMessage(handler) {
      messageHandlers.add(handler);
      return () => {
        messageHandlers.delete(handler);
      };
    },

    onStatusChange(handler) {
      statusHandlers.add(handler);
      return () => {
        statusHandlers.delete(handler);
      };
    },

    getStatus() {
      return status;
    },

    close() {
      stopHeartbeat();
      ws.close();
      messageHandlers.clear();
      statusHandlers.clear();
      for (const [, p] of pendingRequests) {
        p.reject(new Error('Connection closed'));
      }
      pendingRequests.clear();
      for (const p of pendingPairings) {
        p.reject(new Error('Connection closed'));
      }
      pendingPairings.clear();
    },

    reconnect() {
      if (healthStrategy === 'circuit-breaker') {
        consecutiveFailures = 0;
        firstFailureAt = 0;
        circuitBroken = false;
      }
      ws.reconnect();
    },
  };
}
